import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/LanguageContext';
import promoVideo from '../../../Assets/school_presentation/Spellstudy-promo_9x16_web.mp4';
import promoPoster from '../../../Assets/school_presentation/Spellstudy-promo_poster.jpg';

const HeroVideoModal = ({ onClose, fullscreenOnOpen = false }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const phoneRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const p = vid.play();
    if (p) p.catch(() => {});
  }, []);

  // On mobile we go straight to true browser fullscreen while the play tap's
  // user-gesture activation is still fresh, instead of the small windowed view.
  useLayoutEffect(() => {
    if (fullscreenOnOpen && phoneRef.current?.requestFullscreen) {
      phoneRef.current.requestFullscreen().catch(() => {});
    }
  }, [fullscreenOnOpen]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const active = document.fullscreenElement === phoneRef.current;
      setIsFullscreen(active);
      // the windowed view never renders on mobile, so exiting fullscreen
      // there means the visitor is done watching
      if (!active && fullscreenOnOpen) onClose();
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [fullscreenOnOpen, onClose]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !document.fullscreenElement) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleReplay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.currentTime = 0;
    const p = vid.play();
    if (p) p.catch(() => {});
  };

  const handleToggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (phoneRef.current?.requestFullscreen) {
      phoneRef.current.requestFullscreen().catch(() => {});
    }
  };

  return (
    <div className="lp-video-modal-backdrop" onClick={onClose}>
      <button className="lp-video-modal-close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label={t('landing.hero.closeVideo')}>
        <i className="fa-solid fa-xmark" />
      </button>
      <div className="lx-phone" ref={phoneRef} onClick={(e) => e.stopPropagation()}>
        <video
          ref={videoRef}
          src={promoVideo}
          poster={promoPoster}
          muted={muted}
          loop
          playsInline
          preload="metadata"
        />
        <div className="lx-phone-controls">
          <button
            className="lx-phone-sound"
            onClick={() => setMuted(!muted)}
            aria-label={muted ? t('landing.hero.unmute') : t('landing.hero.mute')}
          >
            <i className={`fa-solid ${muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
          </button>
          <button
            className="lx-phone-sound"
            onClick={handleReplay}
            aria-label={t('landing.hero.replay')}
          >
            <i className="fa-solid fa-rotate-right" />
          </button>
        </div>
        <button
          className="lx-phone-fullscreen"
          onClick={handleToggleFullscreen}
          aria-label={isFullscreen ? t('landing.hero.exitFullscreen') : t('landing.hero.fullscreen')}
        >
          <i className={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} />
        </button>
      </div>
    </div>
  );
};

export default HeroVideoModal;
